<!-- Sino da topbar: contador de não lidas e, ao clicar, o popover com uma
     notificação por linha.

     - Abrir marca tudo como lido (o contador some). As que eram novas continuam
       destacadas enquanto o popover estiver aberto, para a pessoa ver o que
       chegou.
     - Convite pendente tem "Ver convite" (abre o modal com o consentimento,
       Aceitar e Recusar) e não se apaga, nem no × nem no "Limpar": é o único
       lugar onde se responde.
     - A lista rola sozinha quando passa da altura máxima; o cabeçalho com o
       "Limpar" fica sempre visível.

     Posicionamento: o canônico dos popovers (useAnchoredPanel, teleportado,
     position: fixed), alinhado pela direita porque o sino fica no canto. -->
<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useAnchoredPanel } from "@/composables/useAnchoredPanel";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { formatRelative } from "@/utils/dateUtils";
import { splitBold } from "@/utils/stringUtils";
import InvitationAcceptModal from "@/components/Notifications/InvitationAcceptModal.vue";

const router = useRouter();
const store = useNotificationStore();

const { isOpen, triggerRef, panelRef, panelStyle, toggle, close } =
  useAnchoredPanel({ align: "right" });

onMounted(() => store.startPolling());
onBeforeUnmount(() => store.reset());

// Ids que estavam não lidos quando o popover abriu: continuam destacados até fechar.
const freshIds = ref(new Set());
const errorMsg = ref("");
const openInvite = ref(null);

async function onToggle() {
  const abrindo = !isOpen.value;
  toggle();
  if (!abrindo) return;
  errorMsg.value = "";
  freshIds.value = new Set(
    store.items.filter((n) => !n.isRead).map((n) => n.notificationId)
  );
  try {
    await store.markAllRead();
  } catch {
    // Se falhar, o contador volta na próxima consulta; nada a mostrar.
  }
}

const badge = computed(() =>
  store.unreadCount > 9 ? "9+" : String(store.unreadCount)
);
const hasRemovable = computed(() => store.items.some((n) => n.canDelete));

function isPendingInvite(n) {
  return n.type === "member_invite" && n.invitationStatus === "pending";
}

// Avisos para quem administra o círculo: a resposta tem atalho para os convites.
function isCircleAnswer(n) {
  return ["invite_accepted", "invite_declined", "invite_no_seat"].includes(n.type);
}

function iconOf(n) {
  if (n.type === "invite_no_seat") return "fa-exclamation-triangle notif-icon--warn";
  if (n.type === "invite_accepted" || n.invitationStatus === "accepted")
    return "fa-check-circle notif-icon--ok";
  if (n.type === "invite_declined" || n.invitationStatus === "declined")
    return "fa-times-circle notif-icon--bad";
  if (n.invitationStatus === "expired") return "fa-clock notif-icon--muted";
  return "fa-envelope notif-icon--info";
}

function showInvite(n) {
  close();
  openInvite.value = n;
}

function goToInvitations() {
  close();
  router.push({ name: "MemberAccountForm", query: { tab: "convites" } });
}

async function remove(n) {
  errorMsg.value = "";
  try {
    await store.remove(n.notificationId);
  } catch (err) {
    errorMsg.value =
      err.response?.data?.message || "Não foi possível apagar a notificação.";
  }
}

async function clearAll() {
  errorMsg.value = "";
  try {
    await store.clear();
  } catch {
    errorMsg.value = "Não foi possível limpar as notificações.";
  }
}
</script>

<template>
  <div class="notif">
    <button
      ref="triggerRef"
      type="button"
      class="notif-trigger"
      :class="{ 'notif-trigger--open': isOpen }"
      :title="'Notificações'"
      :aria-label="
        store.unreadCount
          ? `Notificações, ${store.unreadCount} não lidas`
          : 'Notificações'
      "
      aria-haspopup="dialog"
      :aria-expanded="isOpen"
      @click="onToggle"
    >
      <i class="fas fa-bell" aria-hidden="true"></i>
      <span v-if="store.unreadCount" class="notif-badge" aria-hidden="true">{{
        badge
      }}</span>
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="panelRef"
        class="anchored-panel notif-panel"
        role="dialog"
        aria-label="Notificações"
        :style="panelStyle"
      >
        <div class="notif-head">
          <span class="notif-title">Notificações</span>
          <button
            v-if="hasRemovable"
            type="button"
            class="btn-link"
            @click="clearAll"
          >
            Limpar
          </button>
        </div>

        <p v-if="errorMsg" class="notif-error" role="alert">{{ errorMsg }}</p>

        <div v-if="!store.items.length" class="notif-empty">
          <i class="fas fa-bell" aria-hidden="true"></i>
          <span>Nenhuma notificação.</span>
        </div>

        <!-- Só a lista rola; a altura máxima acompanha a janela. -->
        <ul v-else class="notif-list scroll-soft">
          <li
            v-for="n in store.items"
            :key="n.notificationId"
            class="notif-item"
            :class="{ 'notif-item--fresh': freshIds.has(n.notificationId) }"
          >
            <span
              class="notif-dot"
              :class="{ 'notif-dot--on': freshIds.has(n.notificationId) }"
              aria-hidden="true"
            ></span>
            <i class="fas notif-icon" :class="iconOf(n)" aria-hidden="true"></i>
            <div class="notif-body">
              <span class="notif-text"
                ><template v-for="(part, i) in splitBold(n.message)" :key="i"
                  ><strong v-if="part.bold">{{ part.text }}</strong
                  ><template v-else>{{ part.text }}</template></template
                ></span
              >
              <span class="notif-time">{{ formatRelative(n.createdAt) }}</span>

              <div v-if="isPendingInvite(n)" class="notif-actions">
                <button
                  type="button"
                  class="btn btn-primary btn-sm"
                  @click="showInvite(n)"
                >
                  Ver convite
                </button>
              </div>
              <button
                v-else-if="isCircleAnswer(n)"
                type="button"
                class="btn-link notif-go"
                @click="goToInvitations"
              >
                Ver convites <i class="fas fa-arrow-right" aria-hidden="true"></i>
              </button>
            </div>
            <button
              v-if="n.canDelete"
              type="button"
              class="btn-icon notif-remove"
              aria-label="Apagar notificação"
              @click="remove(n)"
            >
              <i class="fas fa-times" aria-hidden="true"></i>
            </button>
          </li>
        </ul>

        <p v-if="store.items.some((n) => !n.canDelete)" class="notif-foot">
          Convite sem resposta fica aqui até você aceitar ou recusar.
        </p>
      </div>
    </Teleport>

    <InvitationAcceptModal
      v-if="openInvite"
      :notification="openInvite"
      @close="openInvite = null"
    />
  </div>
</template>

<style scoped>
.notif {
  display: inline-flex;
  align-self: flex-end;
}

/* Mesmo formato dos botões redondos da topbar (WhatsApp, plano). */
.notif-trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.3rem 0.8rem;
  border: none;
  border-radius: var(--radius-pill);
  background-color: var(--brand-soft);
  color: var(--text-on-brand-soft);
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color var(--transition-base), transform var(--transition-press);
}
.notif-trigger:hover,
.notif-trigger--open {
  background-color: var(--brand-soft-hover);
}
.notif-trigger:active {
  transform: translateY(1px);
}
/* Tamanho e cor do ícone eram de uma regra solta do DashboardLayout
   (.user-controls .fa-bell); ficam aqui, no dono. O WhatsApp e o foguete ao
   lado usam o mesmo 0.9rem. */
.notif-trigger i {
  font-size: 0.9rem;
  width: 0.9rem;
  text-align: center;
  color: var(--text-dark);
}
.notif-trigger:focus,
.notif-trigger:focus-visible {
  outline: none;
  box-shadow: none;
}
.notif-badge {
  position: absolute;
  top: -5px;
  right: -4px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  box-sizing: border-box;
  border-radius: var(--radius-pill);
  background: var(--danger);
  color: var(--text-on-danger);
  font-size: 0.65rem;
  font-weight: var(--font-bold);
  line-height: 16px;
  text-align: center;
}

/* Superfície do popover; position: fixed e z-index vêm de .anchored-panel. */
.notif-panel {
  width: min(400px, calc(100vw - 2 * var(--space-5)));
  background-color: var(--surface);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-dropdown);
  overflow: hidden;
}
.notif-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4) var(--space-5);
  border-bottom: 1px solid var(--border-color);
}
.notif-title {
  font-size: var(--text-base);
  font-weight: var(--font-semibold);
  color: var(--text-dark);
}
.notif-error {
  margin: 0;
  padding: var(--space-3) var(--space-5);
  background: var(--danger-bg);
  color: var(--danger-text);
  font-size: var(--text-sm);
}

/* A lista cresce até o teto e daí em diante rola (barra fina do .scroll-soft);
   o teto acompanha a altura da janela para o popover nunca sair dela. */
.notif-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: min(480px, calc(100vh - 180px));
}
.notif-item {
  position: relative;
  display: flex;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-5);
  border-bottom: 1px solid var(--border-color);
}
.notif-item:last-child {
  border-bottom: none;
}
.notif-item--fresh {
  background: var(--brand-tint);
}
.notif-dot {
  width: 8px;
  height: 8px;
  margin-top: 6px;
  flex-shrink: 0;
  border-radius: var(--radius-pill);
}
.notif-dot--on {
  background: var(--brand);
}
.notif-icon {
  flex-shrink: 0;
  width: 16px;
  margin-top: 3px;
  text-align: center;
}
.notif-icon--info {
  color: var(--text-brand);
}
.notif-icon--ok {
  color: var(--success-text);
}
.notif-icon--warn {
  color: var(--warning-text);
}
.notif-icon--bad {
  color: var(--danger-text);
}
.notif-icon--muted {
  color: var(--text-light);
}
.notif-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding-right: var(--space-5);
}
.notif-text {
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
  color: var(--text-dark);
  white-space: normal;
}
.notif-time {
  font-size: var(--text-xs);
  color: var(--text-light);
}
.notif-actions {
  display: flex;
  gap: var(--space-2);
}
.notif-go {
  align-self: flex-start;
}
.notif-go i {
  font-size: 0.7rem;
}
.notif-remove {
  position: absolute;
  top: var(--space-2);
  right: var(--space-2);
  width: 26px;
  height: 26px;
}
.notif-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-8) var(--space-5);
  color: var(--text-light);
  font-size: var(--text-sm);
}
.notif-empty i {
  font-size: 1.5rem;
}
.notif-foot {
  margin: 0;
  padding: var(--space-3) var(--space-5);
  border-top: 1px solid var(--border-color);
  background: var(--surface-2);
  font-size: var(--text-xs);
  color: var(--text-light);
}
</style>
